import { useEffect, useRef } from 'react';
import { useSandboxStore } from '../store/sandboxStore';
import { useAuthenticatedApi } from './useAuthenticatedApi';
import { apiEndpoints } from '../utils/api';

/**
 * Lightweight hook for sandbox state management using centralized Zustand store.
 * Replaces useEnsureSandbox with a more efficient implementation that avoids redundant API calls.
 * 
 * @param {string} workspaceId - The ID of the workspace
 * @param {boolean} enabled - Whether to enable the hook (default: true)
 * @returns {Object} Object containing sandbox status and operations
 */
export const useSandbox = (workspaceId, enabled = true) => {
  const api = useAuthenticatedApi();
  const apiRef = useRef(api);
  
  // Update ref when api changes
  useEffect(() => {
    apiRef.current = api;
  }, [api]);
  
  // Get store state and actions
  const sandboxStatus = useSandboxStore(
    state => state.sandboxStatuses[workspaceId] || { 
      status: 'idle',
      sandboxId: null,
      taskId: null,
      message: null,
      lastUpdated: Date.now()
    }
  );
  
  const subscribeToWorkspace = useSandboxStore(state => state.subscribeToWorkspace);
  const resetSandboxInStore = useSandboxStore(state => state.resetSandbox);
  const setSandboxStatus = useSandboxStore(state => state.setSandboxStatus);
  
  // Subscribe to polling when component mounts
  useEffect(() => {
    if (!workspaceId || !enabled) return;

    if (process.env.NODE_ENV === 'development') {
      console.log(`[useSandbox] Subscribing to workspace ${workspaceId}`);
    }

    // Subscribe (will start polling if first subscriber)
    const unsubscribe = subscribeToWorkspace(workspaceId, apiRef.current);

    // Cleanup (will stop polling if last subscriber)
    return () => {
      if (process.env.NODE_ENV === 'development') {
        console.log(`[useSandbox] Unsubscribing from workspace ${workspaceId}`);
      }
      unsubscribe();
    };
  }, [workspaceId, enabled, subscribeToWorkspace]);
  
  // Manual retry function
  const retry = async () => {
    if (!workspaceId) return;
    
    if (process.env.NODE_ENV === 'development') {
      console.log(`[useSandbox] Manual retry for workspace ${workspaceId}`);
    }
    setSandboxStatus(workspaceId, { status: 'loading', message: 'Retrying...' });
    
    // Force an immediate poll
    try {
      const response = await apiRef.current.post(apiEndpoints.workspaceEnsureSandbox(workspaceId));
      
      if (response.status === 'not_applicable') {
        setSandboxStatus(workspaceId, {
          status: 'not_applicable',
          sandboxId: null,
          taskId: null,
          message: response.message || 'This workspace does not use sandboxes',
        });
      } else if (response.status === 'ready') {
        setSandboxStatus(workspaceId, {
          status: 'ready',
          sandboxId: response.sandbox_id || null,
          taskId: null,
          message: 'Sandbox is ready',
        });
      } else if (response.status === 'creating' || response.status === 'resuming') {
        setSandboxStatus(workspaceId, {
          status: response.status,
          sandboxId: null,
          taskId: response.task_id || null,
          message: response.message || `Sandbox is ${response.status}...`,
        });
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('[useSandbox] Failed to retry sandbox status:', error);
      }
      setSandboxStatus(workspaceId, {
        status: 'error',
        message: error instanceof Error ? error.message : 'Failed to check sandbox status',
      });
    }
  };
  
  // Reset sandbox function
  const resetSandbox = async () => {
    if (!workspaceId) return;
    
    if (process.env.NODE_ENV === 'development') {
      console.log(`[useSandbox] Resetting sandbox for workspace ${workspaceId}`);
    }
    resetSandboxInStore(workspaceId);
    
    try {
      await apiRef.current.post(apiEndpoints.workspaceResetSandbox(workspaceId));
      // The polling will automatically pick up the new status
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('[useSandbox] Failed to reset sandbox:', error);
      }
      setSandboxStatus(workspaceId, {
        status: 'error',
        message: error instanceof Error ? error.message : 'Failed to reset sandbox',
      });
      throw error; // Re-throw to allow caller to handle
    }
  };
  
  // For backward compatibility with existing components
  const ensureSandbox = retry;
  
  return {
    // State
    sandboxStatus: {
      status: sandboxStatus.status,
      sandboxId: sandboxStatus.sandboxId,
      taskId: sandboxStatus.taskId,
      message: sandboxStatus.message,
    },
    error: sandboxStatus.status === 'error' ? new Error(sandboxStatus.message || 'Unknown error') : null,
    
    // Functions
    ensureSandbox, // For backward compatibility
    retry,
    resetSandbox,
    
    // Status flags
    isLoading: sandboxStatus.status === 'loading',
    isCreating: sandboxStatus.status === 'creating',
    isResuming: sandboxStatus.status === 'resuming',
    isReady: sandboxStatus.status === 'ready',
    isSleeping: sandboxStatus.status === 'sleeping',
    isError: sandboxStatus.status === 'error',
    isNotApplicable: sandboxStatus.status === 'not_applicable',
    
    // For backward compatibility (not used in new implementation)
    isAutoRetrying: false,
    retryCount: 0,
    maxRetries: 6
  };
};

// Export for backward compatibility
export default useSandbox; 